import { testCandidates } from '@/data/candidates/test-candidates'
import {
  ballotColors,
  getCandidateDignityGroupId,
  isNationalCandidateDignity,
} from '@/lib/data/ballotColors'
import type { Candidate } from '@/types/candidate'

const apiUrl = import.meta.env.VITE_VOTE_PRIORITY_API_URL
const productionCandidateFallback: Candidate[] = []

interface CandidateApiResponse {
  ok?: boolean
  candidates?: unknown
}

function isCandidate(value: unknown): value is Candidate {
  if (!value || typeof value !== 'object') return false

  const candidate = value as Partial<Candidate>
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.slug === 'string' &&
    typeof candidate.fullName === 'string' &&
    typeof candidate.photoUrl === 'string' &&
    typeof candidate.provinceId === 'string' &&
    typeof candidate.provinceName === 'string' &&
    typeof candidate.cantonId === 'string' &&
    typeof candidate.cantonName === 'string' &&
    typeof candidate.dignityId === 'string' &&
    typeof candidate.dignityName === 'string' &&
    (candidate.age === undefined || typeof candidate.age === 'number') &&
    (candidate.profession === undefined || typeof candidate.profession === 'string') &&
    typeof candidate.fixture === 'boolean' &&
    Array.isArray(candidate.proposals) &&
    candidate.proposals.every(
      (proposal) =>
        proposal &&
        typeof proposal.id === 'string' &&
        typeof proposal.title === 'string' &&
        typeof proposal.summary === 'string',
    )
  )
}

export async function getManagedCandidates(): Promise<Candidate[]> {
  if (!apiUrl) return getCandidateFallback()

  const url = new URL(apiUrl)
  url.searchParams.set('action', 'candidates')

  const response = await fetch(url.toString())
  if (!response.ok) throw new Error('No se pudieron cargar los candidatos')

  const payload = (await response.json()) as CandidateApiResponse
  const candidates = Array.isArray(payload.candidates)
    ? payload.candidates.filter(isCandidate)
    : []

  return payload.ok === true && candidates.length ? candidates : getCandidateFallback()
}

export function getTestCandidates(): Candidate[] {
  return testCandidates
}

export function getCandidateFallback(): Candidate[] {
  return import.meta.env.DEV ? testCandidates : productionCandidateFallback
}

export function getCandidateDignityOptions(candidates: Candidate[] = testCandidates) {
  const catalogOptions = ballotColors.map((dignity) => ({
    id: dignity.id,
    label: dignity.shortLabel,
  }))
  const catalogGroups = new Set(catalogOptions.map((option) => option.id))
  const additionalOptions = Array.from(
    new Map(
      candidates.map((candidate) => [
        candidate.dignityId,
        {
          id: candidate.dignityId,
          label: candidate.dignityName,
        },
      ]),
    ).values(),
  )
    .filter((option) => !catalogGroups.has(getCandidateDignityGroupId(option.id)))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'))

  return [...catalogOptions, ...additionalOptions]
}

export function getCandidatesByTerritory(filters: {
  provinceId?: string
  cantonId?: string
  dignityId?: string
  politicalOrganizationId?: string
}, candidates: Candidate[] = testCandidates) {
  const filtersByTerritory = !filters.dignityId || !isNationalCandidateDignity(filters.dignityId)

  return candidates.filter((candidate) => {
    if (filtersByTerritory && filters.provinceId && candidate.provinceId !== filters.provinceId) {
      return false
    }
    if (filtersByTerritory && filters.cantonId && candidate.cantonId !== filters.cantonId) {
      return false
    }
    if (
      filters.dignityId &&
      getCandidateDignityGroupId(candidate.dignityId) !==
        getCandidateDignityGroupId(filters.dignityId)
    ) {
      return false
    }
    if (
      filters.politicalOrganizationId &&
      candidate.politicalOrganizationId !== filters.politicalOrganizationId
    ) {
      return false
    }

    return true
  })
}

export function areCandidatesComparable(reference: Candidate, candidate: Candidate) {
  const dignityGroup = getCandidateDignityGroupId(reference.dignityId)
  if (dignityGroup !== getCandidateDignityGroupId(candidate.dignityId)) return false

  if (dignityGroup === 'prefecto') {
    return reference.provinceId === candidate.provinceId
  }

  if (
    dignityGroup === 'alcalde' ||
    dignityGroup === 'concejales-urbanos' ||
    dignityGroup === 'concejales-rurales'
  ) {
    return (
      reference.provinceId === candidate.provinceId &&
      reference.cantonId === candidate.cantonId
    )
  }

  return true
}

export function getComparableCandidates(
  reference: Candidate,
  candidates: Candidate[] = testCandidates,
) {
  return candidates.filter(
    (candidate) => candidate.id !== reference.id && areCandidatesComparable(reference, candidate),
  )
}

export function getCandidateBySlug(
  slug: string,
  candidates: Candidate[] = testCandidates,
): Candidate | undefined {
  return candidates.find((candidate) => candidate.slug === slug)
}
